export * from './instagram-comments';

import { Departments } from '@/types';

export type Platform = 'instagram' | 'telegram';

export type UrlPlatform = 'instagram' | 'telegram' | 'meta';

export const urlToDomainPlatform = (u: UrlPlatform): Platform => (u === 'meta' ? 'instagram' : u);

/** Server route segment for each domain platform — Instagram pages live under `/meta/pages/*`. */
export const PLATFORM_URL_SEGMENT: Record<Platform, string> = {
  instagram: 'meta',
  telegram: 'telegram',
};

export type Integration = 'umnico' | 'instagram' | 'facebook-messenger' | 'instagram-connect';

export type QrAuthStatus = 'pending' | 'waitingScan' | 'passwordRequired' | 'ready' | 'failed';

export type QrAuthState = {
  pageId: string;
  status: QrAuthStatus;
  link?: string;
  updatedAt: number;
};

export type TgPage = {
  _id: string;
  phone?: string;
  botToken?: string;
  name: string;
  username?: string;
  department: Departments;
  account: {
    _id: string;
  };
  profile?: {
    _id: string;
  };
  config: {
    isConnected: boolean;
    isImporting?: boolean;
    importingFrom?: number;
    isHotDialogNotificationEnabled?: boolean;
    isArchived: boolean;
    isRealTimeStatsEnabled?: boolean;
    isRestoring?: boolean;
  };
  archiveDataKey?: string;
  archivedAt?: number;
  deletedAt?: number;
};

export type InstPage = {
  _id: string;
  name: string;
  username?: string;
  department?: Departments;
  integration?: Integration;
  inst: {
    id: string;
    fbId?: string;
    fbToken?: string;
    /** IG User Token expiry, epoch ms (integration: 'instagram-connect') */
    igTokenExpiresAt?: number;
  };
  umnico?: {
    accountId: number;
    saId: number;
    employeeId: number;
  };
  account: {
    _id: string;
  };
  profile?: {
    _id: string;
  };
  config: {
    isConnected: boolean;
    isImporting?: boolean;
    importingFrom?: number;
    isHotDialogNotificationEnabled?: boolean;
    isArchived: boolean;
    isRestoring?: boolean;
    isRealTimeStatsEnabled?: boolean;
  };
  archiveDataKey?: string;
  archivedAt?: number;
  deletedAt?: number;
};

/**
 * Meta pages all come from a single `/meta/pages` endpoint under `Platforms.instagram`,
 * so `integration` is the only thing separating Facebook Messenger from the Instagram channels.
 */
export const isFacebookMessengerPage = (page: InstPage) =>
  page.integration === 'facebook-messenger';

export type Funnel = {
  _id: string;
  page: {
    _id: string;
  };
  name: string;
  color: string;
  order: number;
  deletedAt?: number;
};

export type TgManager = {
  _id: string;
  account: {
    _id: string;
  };
  page: {
    _id: string;
  };
  username: string;
};

export type AddInstFacebookPagesData = {
  token: string;
  department: Departments;
};

export type AddInstConnectPageData = {
  code: string;
  redirectUri: string;
  department?: Departments;
};

export type AddInstViaSystemUserPagesData = {
  department: Departments;
};

export type EditInstPageData = Partial<Pick<InstPage, 'name' | 'username' | 'department'>>;

export type AddTgPageData = {
  phone: string;
  name: string;
  username?: string;
  department: Departments;
};

export type EditTgPageData = Partial<Pick<TgPage, 'name' | 'username' | 'phone'>>;

export type RefreshFbPageTokenData = { pageId: string };

export type AddFunnelData = {
  pageId: string;
  name: string;
  color: string;
};
export type RemoveFunnelData = (params: { funnelId: string; pageId: string }) => Promise<void>;

export type EditFunnelData = Omit<Funnel, 'page' | 'order'>;

export type SignUpManagerData = {
  pageId: string;
  platform: Platform;
  username: string;
  password: string;
  department: Departments;
};

export type DeleteManagerData = {
  managerId: string;
};

export type ConnectPageData = {
  platform: Platform;
  pageId: string;
  isArchived?: boolean;
  authMethod?: 'qr';
};

export type ImportPageData = {
  pageId: string;
  importingFrom?: number;
};

export type RemovePageData = {
  pageId: string;
  platform: Platform;
};

export type EnableIsHotDialogNotificationData = {
  platform: Platform;
  pageId: string;
};

export type ToggleRealTimeStatsData = {
  platform: Platform;
  pageId: string;
};

export type EnableRealTimeStatsData = ToggleRealTimeStatsData;
export type DisableRealTimeStatsData = ToggleRealTimeStatsData;
